import { Box, Button, Flex, Icon, Text } from '@everlywell/ui-kit';
import { ArrowRight } from '@phosphor-icons/react';
import Price from 'components/Price';
import { PurchaseOptionsDrawerProduct } from 'components/PurchaseOptionsDrawerProvider/PurchaseOptionsDrawerProvider.context';
import { Link as GatsbyLink } from 'gatsby';
import { useFlags } from 'gatsby-plugin-launchdarkly';
import React from 'react';
import { discountedPrice, Promo } from 'utils/helpers/activePromoHelpers';
import { SubscriptionVariantType } from 'utils/types';

import {
  PurchaseOptionType,
  MembershipOptionType,
} from '../PurchaseOptionsDrawer';

export type PreCartFooterProps = {
  selectedOption: PurchaseOptionType;
  variant?: SubscriptionVariantType;
  price: string;
  onClick: (ctaText: string) => void;
  isEverlywellPlus: boolean;
  isLoading: boolean;
  selectedSubscriptionOption?: MembershipOptionType | null;
  product?: PurchaseOptionsDrawerProduct | null | undefined;
  activePromo: Promo | null;
};

/**
 * PreCart Footer.
 */
function PreCartFooter(props: PreCartFooterProps): JSX.Element {
  const {
    selectedOption,
    variant,
    price,
    onClick,
    isEverlywellPlus,
    isLoading,
    selectedSubscriptionOption,
    product,
    activePromo,
  } = props;

  const isSubscription = selectedOption === PurchaseOptionType.SubscribeAndSave;
  const oneTimePurchase = selectedOption === PurchaseOptionType.OneTime;

  const subscriptionFrequency = variant?.subscription_plan?.name.toLowerCase();

  const productPriceNumber = parseFloat(price ?? '0');

  const hasEverlywellCheckout = selectedSubscriptionOption && isEverlywellPlus;

  const discountedPromoPrice = parseFloat(
    discountedPrice(price ?? '0', activePromo ? activePromo : undefined),
  );

  const showPromo =
    Boolean(activePromo && product?.promotionable) &&
    productPriceNumber > discountedPromoPrice;

  const productSlug = product && product.slug;

  const flags = useFlags();
  const customMdpUrl = productSlug
    ? flags.preCartToCustomMdpLandingPage?.[productSlug]
    : null;

  const everlywellPlusLabels =
    selectedSubscriptionOption === MembershipOptionType.MonthlyMembership
      ? {
          frequency: '/ month',
          price: 39.0,
        }
      : {
          frequency: '/ year',
          price: 444.0,
        };

  const getButtonData = () => {
    // VWO Experiment: Check for Everlywell 360 variant at runtime
    const isEverlywell360Variant = 
      typeof window !== 'undefined' && 
      window.vwoEverlywell360Variant === true;

    if (!isEverlywellPlus) {
      return {
        label: 'Add to Cart',
        to: '',
        testId: 'pre-cart-cta-add-to-cart',
      };
    }

    if (hasEverlywellCheckout) {
      return {
        label: 'Join Now',
        to: `/ewplus-checkout/?slug=${selectedSubscriptionOption}`,
        testId: 'pre-cart-cta-join-now',
      };
    }

    return {
      label: 'Join today',
      to: isEverlywell360Variant ? '/360-full-body-test/' : (customMdpUrl || '/monthly-at-home-lab-testing-membership/'),
      testId: 'pre-cart-cta-view-membership-options',
    };
  };

  const buttonData = getButtonData();

  return (
    <Box
      backgroundColor={'tints.lightCream'}
      borderRadius={'24px 24px 0 0'}
      paddingTop={'20px'}
      paddingBottom={'34px'}
      paddingX={6}
      boxShadow="0px 4px 35px 0px rgba(0, 0, 0, 0.2)"
    >
      {!isEverlywellPlus && (
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}
        >
          <Text
            display={'flex'}
            flexDirection={'column'}
            fontWeight={'medium'}
            fontSize={'lg'}
          >
            Subtotal
            <Text color="tints.darkGrey" fontSize={'md'}>
              {isSubscription
                ? `Billed ${subscriptionFrequency}`
                : 'One time purchase'}
            </Text>
          </Text>
          {showPromo && oneTimePurchase ? (
            <Flex flexDirection="column">
              <Flex color="tints.uiGrey" justifyContent="flex-end">
                <Price price={productPriceNumber} strikeThrough />
              </Flex>
              <Flex
                color="utility.sale"
                flexDirection="row"
                justifyContent={'flex-end'}
                alignItems="baseline"
                gap={1}
                flexWrap={'wrap'}
              >
                <Flex flexDirection={'row'} alignItems={'baseline'} gap={1}>
                  <Price price={discountedPromoPrice} />
                  <Text>with code</Text>
                </Flex>
                <Text wordBreak="break-word" overflowWrap="break-word">
                  {activePromo?.code?.toUpperCase()}
                </Text>
              </Flex>
            </Flex>
          ) : (
            <Price price={productPriceNumber} />
          )}
        </Box>
      )}

      {hasEverlywellCheckout && (
        <Box
          display={'flex'}
          flexDirection={'row'}
          justifyContent={'space-between'}
        >
          <Text
            display={'flex'}
            flexDirection={'column'}
            fontWeight={'medium'}
            fontSize={'lg'}
          >
            Subtotal
            <Text color="tints.darkGrey" fontSize={'md'}>
              {everlywellPlusLabels.frequency}
            </Text>
          </Text>
          <Price price={everlywellPlusLabels.price} />
        </Box>
      )}

      <Button
        data-testid={buttonData.testId}
        variant="primary"
        colorScheme="greenCS"
        size="wide"
        marginTop={3}
        rightIcon={isEverlywellPlus ? <Icon as={ArrowRight} /> : undefined}
        as={isEverlywellPlus ? GatsbyLink : undefined}
        to={buttonData.to}
        onClick={() => onClick(buttonData.label)}
        isLoading={isLoading}
      >
        {buttonData.label}
      </Button>
    </Box>
  );
}

export default PreCartFooter;
